// hooks/usePersistentDateRange.js
import { useState, useEffect } from 'react';
import formatDateDDMMYYYY from '../../../utils/formatDate';
import {
  isValid,
  isBefore,
  isAfter,
  subDays
} from 'date-fns';
import { useStatus } from '../../../context/StatusContext';

const isValidDate = (d) => d instanceof Date && isValid(d);

const clampDate = (d, min, max) => {
  let x = d;
  if (min && isBefore(x, min)) x = min;
  if (max && isAfter(x, max)) x = max;
  return x;
};

const clampRange = ({ startDate, endDate }, min, max) => {
  let s = clampDate(startDate, min, max);
  let e = clampDate(endDate, min, max);
  if (isAfter(s, e)) s = e;
  return { startDate: s, endDate: e };
};

const clampRangeArray = (arr, min, max) =>
  (arr || []).map((r) => ({ key: r.key || 'selection', ...clampRange(r, min, max) }));

const getValidRange = (minDate, maxDate) => {
  const rawEnd = maxDate ?? new Date();
  const end = clampDate(rawEnd, minDate, maxDate);

  let start = subDays(end, 5);
  start = clampDate(start, minDate, maxDate);

  if (isAfter(start, end)) start = end;

  return [{ key: 'selection', startDate: start, endDate: end }];
};

const loadOrCalculateRange = (storageKey, minDateStr, maxDateStr) => {
  const minDate = minDateStr ? new Date(minDateStr) : null;
  const maxDate = maxDateStr ? new Date(maxDateStr) : null;

  const effectiveMin = isValidDate(minDate) ? minDate : undefined;
  const effectiveMax = isValidDate(maxDate) ? maxDate : undefined;

  try {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (
        Array.isArray(parsed) &&
        parsed.length > 0 &&
        parsed[0]?.startDate &&
        parsed[0]?.endDate
      ) {
        const loadedRange = parsed.map((range) => ({
          ...range,
          startDate: new Date(range.startDate),
          endDate: new Date(range.endDate),
        }));
        return clampRangeArray(loadedRange, effectiveMin, effectiveMax);
      }
    }
  } catch (err) {
    console.error('Failed to load or parse saved date range:', err);
  }

  return getValidRange(effectiveMin, effectiveMax);
};

const usePersistentDateRange = (storageKey = 'dashboard_date_range') => {
  const { statusValues } = useStatus() || {};
  const minDateStr = statusValues?.minDate || localStorage.getItem('min_date');
  const maxDateStr = statusValues?.maxDate || localStorage.getItem('max_date');

  const [selectedRange, setSelectedRange] = useState(() => {
    return loadOrCalculateRange(storageKey, minDateStr, maxDateStr);
  });

  useEffect(() => {
    setSelectedRange(loadOrCalculateRange(storageKey, minDateStr, maxDateStr));
  }, [minDateStr, maxDateStr, storageKey]);

  const handleDateChange = (range) => {
    setSelectedRange(range);
    localStorage.setItem(storageKey, JSON.stringify(range));
  };

  // Safe destructuring with fallback
  const startDate = isValid(selectedRange?.[0]?.startDate)
    ? selectedRange[0].startDate
    : new Date();

  const endDate = isValid(selectedRange?.[0]?.endDate)
    ? selectedRange[0].endDate
    : new Date();

  const formattedStart = formatDateDDMMYYYY(startDate);
  const formattedEnd = formatDateDDMMYYYY(endDate);

  return {
    selectedRange,
    handleDateChange,
    formattedStart,
    formattedEnd,
    startDate,
    endDate,
  };
};

export default usePersistentDateRange;

